/**
 * @author Samuel Pelletier
 * @copyright (c) 2023 Cube Noir
 * @fileoverview header of a 360 scene, including title and navigation
 * */
/* eslint jsx-a11y/no-noninteractive-tabindex: 0 */
import React, { useCallback, useEffect, useRef } from "react";

import { useRouteData } from "react-static";
import { LitePlaceLang, Navigable, WithPrevState } from "@utils/data";

import { selectPlaces } from "@components/PersistedData/placesSlice";
import { PlaceHeaderProps } from "./index.interface";
import { useUrl } from "../../utils/navigation";
import { Cinematic, CollectableType } from "../../utils/data";
import { useAppSelector } from "../PersistedData/hooks";
import { selectCinematics } from "../PersistedData/cinematicsSlice";
import { selectCollectables } from "../PersistedData/collectablesSlice";
import { useI18n } from "../../I18nextProvider";
import PlaceHeaderButton from "./PlaceHeaderButton";

function PlaceHeader({ place, isFull = false, collectedCount, mainContentOnPopup }: PlaceHeaderProps) {
  const { lang, collectables, places } = useRouteData();
  const refCbMenu = useRef<HTMLInputElement>();

  const i18n = useI18n();
  const placeLang: LitePlaceLang = place[lang];

  const collectableUrl = useUrl({ type: "collectable" });
  const placeUrl = useUrl({ type: "place" });
  const cinematicUrl = useUrl({ type: "cinematic" });
  const historyUrl = useUrl({ type: "history" });

  const { cinematics } = useRouteData();
  const allCinematics: Navigable[] = cinematics.slice();
  const allPages: Navigable[] = collectables.filter((c) => {
    return c.type === CollectableType.Page;
  });
  const allItems: Navigable[] = collectables.filter((c) => {
    return c.type !== CollectableType.Page;
  });

  const isBrowser = typeof document !== "undefined";
  const collectedCinematics = isBrowser ? useAppSelector(selectCinematics) : [];
  const collectedCollectables = isBrowser ? useAppSelector(selectCollectables) : [];
  const collectedPlaces = useAppSelector(selectPlaces) || [];
  const collected = collectedCinematics.concat(collectedCollectables);

  const filteredItems = allItems.filter((item: Navigable) => {
    return collectedCollectables.indexOf(item.id) >= 0;
  });
  const filteredPages = allPages.filter((item: Cinematic) => {
    return collected.indexOf(item.id) >= 0 && item.type === CollectableType.Page;
  });
  const filteredVideos = allCinematics.filter((item: Cinematic) => {
    return collected.indexOf(item.id) >= 0;
  });

  const fullClass = isFull ? "full" : "";

  const getCollectedViewIcons = () => {
    const iconsView = [];
    for (let i = 1; i <= place.interactPoints.length; i++) {
      const useKey = `${place.id}-artefact-marker-${i}`;
      if (i < collectedCount) {
        iconsView.push(<span key={useKey} className="found" />);
      } else if (i === collectedCount) {
        iconsView.push(<span key={useKey} className="found last" />);
      } else {
        iconsView.push(<span key={useKey} className="missing" />);
      }
    }
    return iconsView;
  };

  useEffect(() => {
    const handleWindowResize = () => {
      if (window.innerWidth > 768) {
        if (refCbMenu.current) refCbMenu.current.checked = false;
      }
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  /**
   * Faire une vrai navigationp pour éviter fuites de mémoires
   */
  const gotoPlace = useCallback(
    (e) => {
      /* eslint no-restricted-globals: 0 */
      location.href = placeUrl;
      e.preventDefault();
    },
    [placeUrl]
  );

  return (
    <div className={`placeHeader ${fullClass}`}>
      <input type="checkbox" ref={refCbMenu} role="button" aria-haspopup="true" className="cb-placenav-menu" />
      <div className="placenav-menu-burger" />
      <div className="content">
        <ul data-allow-scroll>
          <li>
            <PlaceHeaderButton
              lang={lang}
              title={i18n.t("collectableList.title")}
              linkTo={collectableUrl}
              btnClass="sac"
              txtFound={i18n.t("placeheader.artefacts_found")}
              itemsFound={filteredItems.length}
              itemsTotal={allItems.length}
            />
          </li>
          <li>
            <PlaceHeaderButton
              lang={lang}
              title={i18n.t("history.title")}
              linkTo={historyUrl}
              btnClass="journal"
              txtFound={i18n.t("placeheader.pages_found")}
              itemsFound={filteredPages.length}
              itemsTotal={allPages.length}
            />
          </li>
          <li>
            <PlaceHeaderButton
              lang={lang}
              title={i18n.t("placeList.title")}
              linkTo={placeUrl}
              btnClass="carte"
              txtFound={i18n.t("placeheader.place_found")}
              itemsFound={collectedPlaces.length}
              itemsTotal={places.length}
              onClick={gotoPlace}
            />
          </li>
          <li>
            <PlaceHeaderButton
              lang={lang}
              title={i18n.t("cinematicList.title")}
              linkTo={cinematicUrl}
              btnClass="camera"
              txtFound={i18n.t("placeheader.video_unlocked")}
              itemsFound={filteredVideos.length}
              itemsTotal={allCinematics.length}
            />
          </li>
        </ul>
        <div className="titrage" tabIndex={0} id={mainContentOnPopup ? null : "maincontent"}>
          <h2>{placeLang.year}</h2>
          <h1>{placeLang.title}</h1>
          <p className="artefactProgressText">
            {i18n.t("placeheader.trouve_artefacts", { nbItems: place.interactPoints.length })}
            {": "}
            <span>{`${collectedCount}`}</span>
            {` / ${place.interactPoints.length}`}
          </p>
          {isFull && <div className="artefactProgressView">{getCollectedViewIcons()}</div>}
        </div>
      </div>
    </div>
  );
}

export default PlaceHeader;
